import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {Button, Card, Col, message, Modal, Popconfirm, Row, Space, Spin, Table, Tag, Tree, Typography} from 'antd';
import {
    DeleteOutlined,
    DownloadOutlined,
    NodeIndexOutlined,
    PlusOutlined,
    ReloadOutlined,
    UploadOutlined
} from '@ant-design/icons';
import {
    createProjectMenu,
    deleteProjectMenu,
    deleteProjectMenuBatch,
    getProjectMenuList,
    reorderProjectMenus
} from '../../api/projectMenuApi';
import ExcelUploadModal from '../ExcelUploadModal';
import ProjectMenuCreateModal from './ProjectMenuCreateModal';
import {AG_GRID_DEFAULT_COL_DEF, formatDateTime} from '../../config/agGridConfig';
import useApiPermission from '../../hooks/useApiPermission';
import PermissionButton from '../common/PermissionButton';
import PermissionGate from '../common/PermissionGate';
import ManagementHeader from '../ManagementHeader';

const { Text } = Typography;

// props: { pjtId }
const ProjectMenusTab = ({ pjtId }) => {
  const [loading, setLoading] = useState(false);
  const [treeLoading, setTreeLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [isExcelModalOpen, setIsExcelModalOpen] = useState(false);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [parentForCreate, setParentForCreate] = useState(null);
  const [selectedKey, setSelectedKey] = useState(null);
  const [selectedMenu, setSelectedMenu] = useState(null);
  const [checkedKeys, setCheckedKeys] = useState([]);
  const [reordering, setReordering] = useState(false);

  // 권한 훅
  const { canCall, loading: permLoading } = useApiPermission();
  const canRead = canCall(getProjectMenuList);
  const canCreate = canCall(createProjectMenu);
  const canDeleteSingle = canCall(deleteProjectMenu);

  // 데이터 로드
  const fetchList = useCallback(async () => {
    if (!pjtId) return;
    setLoading(true); setTreeLoading(true);
    try {
      if (permLoading) return;
      if (!canRead) { setRows([]); message.warning('목록 조회 권한이 없습니다.'); return; }
      const res = await getProjectMenuList(pjtId);
      const list = Array.isArray(res?.data) ? res.data : (Array.isArray(res) ? res : []);
      setRows(list);
      // 선택 유지/동기화
      if (selectedKey) {
        const found = list.find(m => m.pjtMenuId === selectedKey);
        setSelectedMenu(found || null);
        if (!found) setSelectedKey(null);
      }
    } catch (e) {
      // 에러 토스트는 공통 인터셉터에서 처리
    } finally {
      setLoading(false); setTreeLoading(false);
    }
  }, [pjtId, permLoading, canRead, selectedKey]);

  useEffect(() => { fetchList(); }, [fetchList]);

  // 트리 빌드
  const treeData = useMemo(() => {
    const byParent = new Map();
    const list = Array.isArray(rows) ? rows : [];
    list.forEach(m => {
      const pid = m.parentMenuId || null;
      if (!byParent.has(pid)) byParent.set(pid, []);
      byParent.get(pid).push(m);
    });
    const sortBySeq = (arr=[]) => (arr || []).slice().sort((a,b)=> (a.sortSeq ?? 0) - (b.sortSeq ?? 0));
    const toNode = (m) => ({ key: m.pjtMenuId, title: m.menuNm, children: sortBySeq(byParent.get(m.pjtMenuId)).map(toNode) });
    const roots = sortBySeq(byParent.get(null));
    return roots.map(toNode);
  }, [rows]);

  const allMenuIds = useMemo(() => rows.map(m => m.pjtMenuId), [rows]);
  const [expandedKeys, setExpandedKeys] = useState([]);
  useEffect(() => { setExpandedKeys(allMenuIds); }, [allMenuIds]);

  const childMenus = useMemo(() => rows.filter(m => (m.parentMenuId || null) === (selectedMenu?.pjtMenuId || null)), [rows, selectedMenu]);

  // 액션 핸들러
  const handleOpenCreateRoot = () => { if (permLoading || !canCreate) { message.warning('생성 권한이 없습니다.'); return; } setParentForCreate(null); setIsCreateModalOpen(true); };
  const handleOpenCreateChild = (parent = selectedMenu) => { if (permLoading || !canCreate) { message.warning('생성 권한이 없습니다.'); return; } if (!parent) { message.warning('하위 생성할 상위 메뉴를 선택하세요.'); return; } setParentForCreate(parent); setIsCreateModalOpen(true); };
  const handleDeleteSingle = async (record = selectedMenu) => {
    if (!record) { message.warning('삭제할 메뉴를 선택하세요.'); return; }
    if (permLoading || !canDeleteSingle) { message.warning('삭제 권한이 없습니다.'); return; }
    try { await deleteProjectMenu(pjtId, record.pjtMenuId); message.success('삭제되었습니다.'); await fetchList(); }
    catch (e) { /* 에러 토스트는 공통 인터셉터에서 처리 */ }
  };
  const handleBatchDelete = async () => {
    if (!checkedKeys.length) { message.warning('삭제할 메뉴를 선택(체크)하세요.'); return; }
    if (permLoading || !canDeleteSingle) { message.warning('삭제 권한이 없습니다.'); return; }
    Modal.confirm({
      title: '선택 삭제', content: `선택된 ${checkedKeys.length}개 메뉴를 삭제하시겠습니까?`, okText: '삭제', okButtonProps: { danger: true }, cancelText: '취소',
      onOk: async () => { try { await deleteProjectMenuBatch(pjtId, checkedKeys); message.success('선택 메뉴 삭제 완료'); setCheckedKeys([]); await fetchList(); } catch (e) { /* 에러 토스트는 공통 인터셉터에서 처리 */ } }
    });
  };

  const handleTreeSelect = (_keys, info) => {
    const key = info.node?.key; setSelectedKey(key); const m = rows.find(x => x.pjtMenuId === key); setSelectedMenu(m || null);
  };

  const onDrop = async (info) => {
    if (reordering) return;
    if (permLoading || !canCreate) { message.warning('재정렬 권한이 없습니다.'); return; }
    try {
      setReordering(true);
      const dragId = info.dragNode?.key;
      const targetId = info.node?.key;
      if (!dragId || !targetId || dragId === targetId) return;
      const dropToGap = info.dropToGap;
      const targetItem = rows.find(m => m.pjtMenuId === targetId);
      const dragItem = rows.find(m => m.pjtMenuId === dragId);
      if (!targetItem || !dragItem) return;

      const originalParent = dragItem.parentMenuId || null;
      let newParent = null;
      let newOrderSiblings = [];

      if (!dropToGap) {
        // 타겟의 하위로 이동
        newParent = targetItem.pjtMenuId;
        const newParentChildren = rows.filter(m => (m.parentMenuId || null) === newParent && m.pjtMenuId !== dragItem.pjtMenuId).sort((a,b)=> (a.sortSeq ?? 0) - (b.sortSeq ?? 0));
        newOrderSiblings = [...newParentChildren.map(c => c.pjtMenuId), dragItem.pjtMenuId];
      } else {
        // 같은 레벨 이동
        newParent = targetItem.parentMenuId || null;
        const siblings = rows.filter(m => (m.parentMenuId || null) === newParent && m.pjtMenuId !== dragItem.pjtMenuId).sort((a,b)=> (a.sortSeq ?? 0) - (b.sortSeq ?? 0));
        const dropPosArr = info.node.pos.split('-');
        const targetPos = parseInt(dropPosArr[dropPosArr.length -1],10);
        const relative = info.dropPosition - targetPos;
        const insertIndex = Math.max(0, Math.min(siblings.length, siblings.findIndex(s => s.pjtMenuId === targetItem.pjtMenuId) + (relative > 0 ? 1 : 0)));
        const before = siblings.slice(0, insertIndex).map(s=>s.pjtMenuId);
        const after = siblings.slice(insertIndex).map(s=>s.pjtMenuId);
        newOrderSiblings = [...before, dragItem.pjtMenuId, ...after];
      }

      // 커맨드 구성 및 호출
      const affectedParentIds = new Set([originalParent, newParent]);
      const commands = [];
      affectedParentIds.forEach(parentId => {
        const levelItems = rows.filter(m => (m.parentMenuId || null) === parentId).map(m => m.pjtMenuId);
        const ordered = parentId === newParent ? newOrderSiblings : levelItems.filter(id => id !== dragId);
        ordered.forEach((id, idx) => { commands.push({ menuId: id, parentMenuId: parentId, sortSeq: idx }); });
      });
      await reorderProjectMenus(pjtId, commands);
      message.success('메뉴 순서가 변경되었습니다.');
      await fetchList();
    } catch (e) {
      // 재정렬 실패 시 에러 토스트는 공통 인터셉터에서 처리
    } finally {
      setReordering(false);
    }
  };

  // 하위 목록 컬럼
  const childColumns = [
    { title: '메뉴명', dataIndex: 'menuNm', key: 'menuNm' },
    { title: '정렬', dataIndex: 'sortSeq', key: 'sortSeq', width: 80, align: 'center' },
    { title: '사용', dataIndex: 'useYn', key: 'useYn', width: 80, align: 'center', render: (v) => ((v||'Y').toUpperCase()==='Y' ? <Tag color="green">Y</Tag> : <Tag>N</Tag>) },
    { title: '등록', dataIndex: 'regDt', key: 'regDt', width: 160, render: (v) => formatDateTime(v) },
    { title: '수정', dataIndex: 'mdfcnDt', key: 'mdfcnDt', width: 160, render: (v) => formatDateTime(v) },
  ];

  // 헤더 액션
  // 엑셀(XLSX) 내보내기: 선택 메뉴의 하위 목록(childMenus)을 기준으로, 첫 열 UUID 포��
  const handleDownloadExcel = async () => {
    const XLSX = await import('xlsx');
    const makeRows = () => {
      const formatUse = (v) => ((v||'Y').toUpperCase()==='Y' ? 'Y' : 'N');
      const toRow = (m) => [m.pjtMenuId ?? '', m.menuNm ?? '', m.sortSeq ?? '', formatUse(m.useYn), formatDateTime(m.regDt), formatDateTime(m.mdfcnDt)];
      if (selectedMenu) { return childMenus.map(toRow); }
      return rows.map(toRow);
    };
    const header = ['UUID', '메뉴명', '정렬', '사용', '등록', '수정'];
    const data = [header, ...makeRows()];
    const ws = XLSX.utils.aoa_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Menus');
    XLSX.writeFile(wb, `project-menus_${pjtId}.xlsx`);
  };

  const HeaderActions = (
    <Space wrap>
      <PermissionButton type="primary" icon={<PlusOutlined />} onClick={handleOpenCreateRoot} require={createProjectMenu}>루트메뉴</PermissionButton>
      <PermissionButton icon={<PlusOutlined />} onClick={() => handleOpenCreateChild()} disabled={!selectedMenu} require={createProjectMenu}>하위생성</PermissionButton>
      <Popconfirm title="삭제" description="선택된(현재) 메뉴를 삭제하시겠습니까?" okText="삭제" cancelText="취소" onConfirm={() => handleDeleteSingle()} disabled={!selectedMenu} >
        <PermissionButton danger icon={<DeleteOutlined />} disabled={!selectedMenu} require={deleteProjectMenu}>삭제</PermissionButton>
      </Popconfirm>
      <PermissionButton danger icon={<DeleteOutlined />} onClick={handleBatchDelete} disabled={!checkedKeys.length} require={deleteProjectMenu}>선택삭제</PermissionButton>
      <PermissionButton icon={<UploadOutlined />} onClick={() => (permLoading || !canCreate) ? message.warning('업로드 권한이 없습니다.') : setIsExcelModalOpen(true)} disabled={permLoading || !canCreate} require={createProjectMenu}>엑셀 업로드</PermissionButton>
      <Button icon={<DownloadOutlined />} onClick={handleDownloadExcel}>엑셀 다운로드</Button>
      <PermissionButton icon={<ReloadOutlined />} onClick={fetchList} require={getProjectMenuList}>새로고침</PermissionButton>
    </Space>
  );

  return (
    <PermissionGate require={getProjectMenuList}>
      {({allowed}) => (
        <div style={{ opacity: allowed ? 1 : 0.5, pointerEvents: allowed ? 'auto' : 'none' }}>
          <Typography.Title level={4} style={{marginTop:0}}><NodeIndexOutlined /> 프로젝트 메뉴</Typography.Title>
          <Row gutter={[16, 16]}>
            <Col xs={24} md={8} lg={6}>
              <Card title="메뉴 트리" styles={{ body: { maxHeight: 600, overflow: 'auto' } }} extra={treeLoading && <Spin size="small" /> }>
                <Tree
                  treeData={treeData}
                  showLine
                  blockNode
                  selectable
                  checkable
                  draggable
                  onDrop={onDrop}
                  onSelect={handleTreeSelect}
                  selectedKeys={selectedKey ? [selectedKey] : []}
                  checkedKeys={checkedKeys}
                  onCheck={(k) => setCheckedKeys(k.checked || k)}
                  fieldNames={{ title: 'title', key: 'key', children: 'children' }}
                  expandedKeys={expandedKeys}
                  onExpand={setExpandedKeys}
                />
              </Card>
            </Col>
            <Col xs={24} md={16} lg={18}>
              <ManagementHeader icon={<NodeIndexOutlined />} title="프로젝트 메뉴 관리" actions={HeaderActions} description="프로젝트별 메뉴 구조를 생성/삭제/재정렬할 수 있습니다." />
              <Card title={selectedMenu ? `선택 메뉴: ${selectedMenu.menuNm}` : '메뉴 상세'} style={{ overflowX: 'auto' }}>
                {selectedMenu ? (
                  <div style={{ marginBottom: 16 }}>
                    <Space direction="vertical" size={4} style={{ width: '100%' }}>
                      <Text><strong>메뉴 ID:</strong> {selectedMenu.pjtMenuId}</Text>
                      <Text><strong>메뉴명:</strong> {selectedMenu.menuNm}</Text>
                      {/* URL 표시 제거 */}
                      <Text><strong>부모메뉴:</strong> {selectedMenu.parentMenuId || '-'}</Text>
                      <Text><strong>정렬순서:</strong> {selectedMenu.sortSeq ?? '-'}</Text>
                      <Text><strong>사용여부:</strong> {selectedMenu.useYn || '-'}</Text>
                      <Text><strong>등록:</strong> {formatDateTime(selectedMenu.regDt)}</Text>
                      <Text><strong>수정:</strong> {formatDateTime(selectedMenu.mdfcnDt)}</Text>
                    </Space>
                  </div>
                ) : (
                  <Text type="secondary">왼쪽 트리에서 메뉴를 선택하면 상세와 하위 목록이 표시됩니다.</Text>
                )}
                <Table size="small" rowKey="pjtMenuId" columns={childColumns} dataSource={childMenus} pagination={false} loading={loading} locale={{ emptyText: selectedMenu ? '하위 메뉴가 없습니다.' : '메뉴를 선택하세요.' }} />
              </Card>
            </Col>
          </Row>

          <ExcelUploadModal isOpen={isExcelModalOpen} onClose={() => setIsExcelModalOpen(false)} onSuccess={fetchList} agGridColumnDefs={[]} agGridDefaultColDef={AG_GRID_DEFAULT_COL_DEF} fieldMapping={[]} uploadApi={async ()=>[]} excelGuide={'엑셀 업로드 포맷은 별도 가이드 참고'} rowKey="rowKey" />

          <ProjectMenuCreateModal isOpen={isCreateModalOpen} onClose={() => setIsCreateModalOpen(false)} onSuccess={fetchList} pjtId={pjtId} parentMenuId={parentForCreate?.pjtMenuId || null} />
        </div>
      )}
    </PermissionGate>
  );
};

export default ProjectMenusTab;
